import React from 'react';
import {
    ListButton,
    SimpleShowLayout,
    TextField,
    BooleanField,
    SimpleForm,
    TextInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    minValue,
    required
} from 'admin-on-rest';
import FilterWithSubmit from '../admin/FilterWithSubmit';
import ListView from '../components/ListView';
import ListTableContainer from '../components/ListTableContainer';
import {DependentInput} from 'aor-dependent-input';
import NanoEventEmitter from 'nano-event-emitter';
import ShowWithType from '../admin/ShowWithType';
import CreateWithType from '../admin/CreateWithType';
import EditWithType from '../admin/EditWithType';
import ValidatedProductTextInput from '../components/ValidatedProductTextInput';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import ProductCategoryByTypeDependentSelectInput from '../components/ProductCategoryByTypeDependentSelectInput';
import ProductUnitStaticTable from '../components/ProductUnitStaticTable';
import ViewButtonWithType from '../buttons/ViewButtonWithType';
import EditButtonWithType from '../buttons/EditButtonWithType';
import EditTextButtonWithType from '../buttons/EditTextButtonWithType';
import ViewTextButtonWithType from '../buttons/ViewTextButtonWithType';
import ProductUnitsStaticIconButton from '../buttons/ProductUnitsStaticIconButton';
import ProductUnitTable from '../components/ProductUnitTable';
import SearchFilterTextInput from '../components/SearchFilterTextInput';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import {tableStyles} from '../shared/styles-table';
import Avatar from 'material-ui/Avatar';
import {CardActions} from 'material-ui/Card';
import Icon from 'material-ui/svg-icons/action/view-list';
import {RefreshButton} from 'admin-on-rest/lib/mui/button';

export const fuseProductIcon = Icon;

const styles = {
    rowShowField: {minWidth: 100},
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '8.5em', overflow: 'hidden'}
};

const getBasePathUpOne = (basePath) => {
    return basePath
        .split('/')
        .slice(0, -1)
        .join('/');
};

const MakeProductUnitTable = ({record, basePath, source, showButtons, productView}) => {
    const productId = (record.id) ? record.id : null;
    const productType = (record.type) ? record.type : null;
    return (
        <ProductUnitTable
            {...record}
            basePath={getBasePathUpOne(basePath)}
            productId={productId}
            productType={productType}
            showCreate={showButtons.create}
            showDelete={showButtons.delete}
            showEdit={showButtons.edit}
            showView={showButtons.view}
            productView={productView}
        />
    );
};

/*
 * View: List
 */

// Create Filter: Product Type + secondary Category
const ProductFilter = (props) => {
    return (
        <FilterWithSubmit {...props} updateOnChange={true} displayButton={false}>
            <ProductCategoryByTypeDependentSelectInput
                {...props}
                source="category_id"
                dependsOn="type"
                dependsOnFilterValue={true}
                dependentProp="dependsOnValue"
                formReference="filterForm"
                forceReset={true}
                optionValue="id"
                optionText="name"
                label="Browse by Product Category"
                allowEmpty
                style={{...viewStyles.filterInline, ...viewStyles.filterHalf}}
            />
            <ReferenceInput
                label="Browse by Product Type"
                source="type"
                reference="ProductTypes"
                perPage={10000}
                validate={[required]}
                allowEmpty
                alwaysOn
                style={{...viewStyles.filterInline, ...viewStyles.filterThird}}>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    options={{
                        clearable: false,
                        labelKey: 'name',
                        className: 'full-width',
                        placeholder: 'Browse by Product Type'
                    }}
                />
            </ReferenceInput>
            <SearchFilterTextInput
                source="search_name"
                label="Enter Product Name"
                buttonLabel="Search Products"
                formReference="filterForm"
                clearOnFilter={['type', 'category_id']}
                alwaysOn
                style={viewStyles.filterFull}
            />
        </FilterWithSubmit>
    )
};

const ListEventEmitter = NanoEventEmitter.create();

// Set title for List Products
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Products</span>;

const ListDescription = () => {
    return (
        <span>
            <img src="img-diagram-Product-List.png" alt="Diagram of Products"
                 style={viewStyles.diagramRight} className="list-diagram"/>
            <p><strong>Products</strong> provide essential information about Product Units.</p>
            <p>Products are not physical sellable units, but parents to Product Units.</p>
            <p>Select a Product Type to get a list of Products. Sort Products by the columns. Click a Product's View
                icon to view its details, or Edit to modify the Product.</p>
            <p>Click a Product Units icon to view any existing Product Units for that Product. You can view or edit
                Units, or create a new one.</p>
            <p>Search Products by name or browse Products by Type and Category.</p>
            <p>Click <strong>Create</strong> to add a new Product.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <img src="img-diagram-Product-List.png" alt="Diagram of Products"
                 style={viewStyles.diagramRight} className="list-diagram"/>
            <p><strong>Products</strong> provide essential information about Product Units.</p>
            <p>Products are not physical sellable units, but parents to Product Units.</p>
            <p>Select a Product Type to get a list of Products. Sort Products by the columns. Click a Product's View
                icon to view its details.</p>
            <p>Click the Product Units icon to view any existing Product Units for that Product.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

// Set columns and behavior for table
const ListColumns = [
    {source: 'id', label: 'ID', type: 'text'},
    {source: 'name', label: 'Name', type: 'text'},
    {source: 'brand', label: 'Brand', type: 'text'},
    {
        source: '',
        label: '',
        Expander: ({isExpanded}) =>
            <div>
                {isExpanded
                    ? <ProductUnitsStaticIconButton/>
                    : <ProductUnitsStaticIconButton/>}
            </div>,
        expander: true,
        filterable: false,
        sortable: false,
        width: 90
    },
    {source: '', label: '', component: <ViewButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: '', label: '', component: <EditButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: 'type', label: '', style: viewStyles.hidden}
];

const ListColumnsViewOnly = [
    {source: 'id', label: 'ID', type: 'text'},
    {source: 'name', label: 'Name', type: 'text'},
    {source: 'brand', label: 'Brand', type: 'text'},
    {
        source: '',
        label: '',
        Expander: ({isExpanded}) =>
            <div>
                {isExpanded
                    ? <ProductUnitsStaticIconButton/>
                    : <ProductUnitsStaticIconButton/>}
            </div>,
        expander: true,
        filterable: false,
        sortable: false,
        width: 90
    },
    {source: '', label: '', component: <ViewButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: 'type', label: '', style: viewStyles.hidden}
];

// Create secondary row subcomponent for each row
const listSubcomponent = (row) => {
    return (
        <div>
            <ProductUnitStaticTable
                productId={row.original.id}
                productType={row.original.type}
                productUnitPath="/ProductUnits"
                productView="list"
                showCreate={true}
                showDelete={false}
                showEdit={true}
                showView={true}
                style={tableStyles.subcomponent}
            />
        </div>
    );
};

const listSubcomponentViewOnly = (row) => {
    return (
        <div>
            <ProductUnitStaticTable
                productId={row.original.id}
                productType={row.original.type}
                productUnitPath="/ProductUnits"
                productView="list"
                showCreate={false}
                showDelete={false}
                showEdit={false}
                showView={true}
                style={tableStyles.subcomponent}
            />
        </div>
    );
};

export const fuseProductList = (props) => (
    <ListView title={ListTitle} desc={<ListDescription/>}
              filters={<ProductFilter/>}
              requiresFilter={true}
              allowQueryString={false}
              eventEmitter={ListEventEmitter}
              toggleEventName="productUnitToggle"
              currentToggle={{id: '', type: '', expanded: false}}
              {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumns}
            perPage={20}
            currentSort={{field: 'name', order: 'ASC'}}
            subComponent={listSubcomponent}
        />
    </ListView>
);

export const fuseProductListViewOnly = (props) => (
    <ListView title={ListTitle} desc={<ListDescriptionViewOnly/>}
              filters={<ProductFilter/>}
              requiresFilter={true}
              allowQueryString={false}
              eventEmitter={ListEventEmitter}
              toggleEventName="productUnitToggle"
              currentToggle={{id: '', type: '', expanded: false}}
              {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumnsViewOnly}
            perPage={20}
            currentSort={{field: 'name', order: 'ASC'}}
            subComponent={listSubcomponentViewOnly}
        />
    </ListView>
);

/*
 * View: Show
 */

// Set title for Show Product
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.name}` : 'Product'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Product details below. To edit this Product, click <strong>Edit</strong> above.</p>
            <p>You can edit existing Product Units or create a new one for this Product. There must be at least
                one Product Unit Piece set as the default Product Unit.</p>
        </span>
    );
};

const ShowDescriptionViewOnly = () => {
    return (
        <span>
            <p>Review Product details below.</p>
        </span>
    );
};

// Replace Actions for Show Product
const ShowActions = ({basePath, data, canEdit}) => (
    <CardActions style={viewStyles.actions}>
        {canEdit &&
            <EditTextButtonWithType basePath={getBasePathUpOne(basePath)} record={data}/>
        }
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        <RefreshButton/>
    </CardActions>
);

/*
 * Show Product Table
 */
export const fuseProductShow = ({canEdit, ...props}) => (
    <ShowWithType {...props} title={<ShowTitle/>} desc={canEdit ? <ShowDescription/> : <ShowDescriptionViewOnly/>}
                  actions={<ShowActions canEdit={canEdit}/>}>
        <SimpleShowLayout>
            <TextField source="id" label="ID"/>
            <TextField source="name" label="Name of Product"/>
            <TextField source="brand" label="Brand"/>
            <TextField source="default_case_qty" label="Default Case Quantity"/>
            <TextField source="type" label="Type"/>
            <TextField source="category" label="Category" style={viewStyles.rowField}/>
            <TextField source="category_id" label="ID" style={viewStyles.rowField}/>
            <BooleanField source="active" label="Display Product on POS" elStyle={styles.fieldSmall}/>
            {canEdit ? <MakeProductUnitTable showButtons={{'create': true, 'view': true, 'edit': true, 'delete': false}}
                                             productView="show"/> :
                <MakeProductUnitTable showButtons={{'create': false, 'view': true, 'edit': false, 'delete': false}}
                                      productView="show"/>}
        </SimpleShowLayout>
    </ShowWithType>
);

/*
 * View: Create form
 */

// Set title for Create Product
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create a Product</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>A <strong>Product</strong> provides essential information about Product Units. A Product is not a physical
                sellable unit, but a parent to a Product Unit.</p>
            <p>Since a Product Unit can be 1 sellable item or multiple pieces bundled together to be sold, a Product
                helps provide different Product Units with the same essential information such as Type, Brand name,
                default quantities, and Category.</p>
            <p>Products require several options to exist before being created. Before creating a Product, you must
                create at least one of each:&nbsp;
                <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Product Type</a>,&nbsp;&nbsp;
                <a href="#/ProductCategories/create" style={viewStyles.fieldLink}>Category</a>.</p>
            <p>Enter the required fields below.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {active: false};

/*
 * Create Product form
 */
export const fuseProductCreate = (props) => (
    <CreateWithType {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} redirect="show">
            <ReferenceInput
                label="Select a Product Type*"
                source="type"
                reference="ProductTypes"
                perPage={10000}
                required={true}
                allowEmpty>
                <VirtualizedSelectInput
                    addField={true}
                    optionText="name"
                    optionValue="name"
                    options={{
                        clearable: false,
                        labelKey: 'name',
                        placeholder: 'Select a Product Type*',
                        required: true
                    }}
                />
            </ReferenceInput>
            <span style={viewStyles.fieldDescription}>
                Select a <strong>Product Type</strong>.
                <br/>
                (If Type is not available, please exit and go
                    to <a href="#/ProductTypes/create" style={viewStyles.fieldLink}>Add Product Type</a>).
                <TooltipIcon dataFor="ttType"/>
                <ReactTooltip id="ttType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Product Type</strong> is used for configuring the POS System to display products to be
                    sold dependent on your Location's DBA. In most cases, a business will only have one Product Type.
                </ReactTooltip>
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <DependentInput dependsOn="type">
                <ProductCategoryByTypeDependentSelectInput
                    {...props}
                    source="category_id"
                    dependentProp="dependsOnValue"
                    optionValue="id"
                    optionText="name"
                    label="Select a Product Category"
                    validate={[required]}
                    allowEmpty/>
                <span style={viewStyles.fieldDescription}>
                    Select a <strong>Product Category</strong> or a subcategory of this Product Type.
                    <br/>
                    (If Category is not available, please exit and go
                    to <a href="#/ProductCategories/create" style={viewStyles.fieldLink}>Add Product Category</a>).
                    <TooltipIcon dataFor="ttCategory"/>
                    <ReactTooltip id="ttCategory" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <strong>Product Categories</strong> are used for sorting between Products on the POS System so
                        employees can browse quickly for the Product they need.
                    </ReactTooltip>
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </DependentInput>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <DependentInput dependsOn="type">
                <span style={viewStyles.fieldDescription}>
                    Enter a unique <strong>Product ID</strong> number.
                    <TooltipIcon dataFor="ttId"/>
                    <ReactTooltip id="ttId" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        Enter a unique <strong>Product ID</strong> number. In most cases, a barcode for
                        a <em>Piece</em>, <em>Mid</em> and <em>Case</em> will have the same number as its Product but
                        with an extra digit.
                        <br/><br/>
                        For example, a Product with Product ID 1234 might have a Product
                        Unit <em>Piece</em> barcode of 12341, a Product Unit <em>Mid</em> of 12342, and a Product
                        Unit <em>Case</em> of 12345.
                    </ReactTooltip>
                </span>
                <ValidatedProductTextInput
                    formName="record-form"
                    source="id"
                    parentField="type"
                    fieldType="text"
                    label="ID*"
                    buttonLabel="Check Availability"
                    successMessage="This Product ID / Barcode is available."
                    errorDuplicateMessage="A Product with this Barcode already exists."
                    errorRequiredMessage="A unique ID / Barcode is required."
                    fieldResource="Products"
                    isRequired={true}
                />
            </DependentInput>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <hr style={viewStyles.divider}/>

            <TextInput source="name" label="Name of Product" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Name</strong> of the the Product.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    In most cases, the <strong>Name</strong> of a Product will be the same as its Product Units. You may have an instance
                    where a Product Unit's Name is different than its Product name.
                </ReactTooltip>
            </span>

            <TextInput source="brand" label="Brand" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                Enter a <strong>Brand</strong> name.
                <TooltipIcon dataFor="ttBrand"/>
                <ReactTooltip id="ttBrand" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Some Products might have the same name but are made by different companies. You can use
                    the <strong>Brand</strong> field to differentiate between different products of the same name.
                    <br/><br/>
                    For example, Product "Banana" has a brand name of "Dole", while another Product "Banana" has a
                    brand name of "Chiquita".
                </ReactTooltip>
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <NumberInput source="default_case_qty" label="Default Case Quantity" defaultValue={1} step={1}
                         validate={[required, minValue(0, 'Must not be a negative number')]}/>
            <span style={viewStyles.fieldDescription}>
                <strong>Default Case Qty</strong> defines the quantity of Pieces in a <em>Case</em> for the
                Product.
                <TooltipIcon dataFor="ttQty"/>
                <ReactTooltip id="ttQty" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    This value must be at least 1. <strong>Default Case Qty</strong> could be how many Pieces are
                    placed on a pallet for shipping, or a specific total of pieces for packaging Cases to be sold.
                    <br/><br/>
                    Depending on how you manage your inventory or stores, you may want to view your Product by
                    Cases instead of Pieces so you can keep track of how many Cases are shipped between Locations,
                    or how many cases are sold. Default Case Qty defines that number and uses it for calculating
                    Inventory and Sale Data in your Reports System.
                </ReactTooltip>
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <SelectInput source="active" label="Display Product on POS"
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'}
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Selecting <em>Yes</em> will display the Product on the POS with the ability to be sold.
                    <br/>
                    Selecting <em>No</em> will remove the Product from the POS so it can not be selected or sold.
                    <br/><br/>
                    In some cases, you may want to create Products that will not be displayed because they are not
                    at your Location(s) yet. Also, a discontinued Product can be removed from your Location(s).
                    This mitigates human error from having employees accidently sell Products that are not in your
                    Location(s).
                </ReactTooltip>
            </span>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the detail Screen for this
                Product. There you can create the first default Product Unit associated with this Product.
            </span>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </CreateWithType>
);

/*
 * View: Edit form
 */

// Set title for Edit Product
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'Product'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify Product fields below. Click <strong>Save</strong> to store this Product.</p>
            <p>You can view existing Product Units for this Product, but this form is for editing the Product
                itself. To edit or add Product Units, click the <strong>Show</strong> button.</p>
        </span>
    );
};

// Replace Actions for Edit Product
const EditActions = ({basePath, data}) => (
    <CardActions style={viewStyles.actions}>
        <ViewTextButtonWithType basePath={getBasePathUpOne(basePath)} record={data}/>
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        <RefreshButton/>
    </CardActions>
);

/*
 * Edit Product form
 */
export const fuseProductEdit = (props) => (
    <EditWithType {...props} title={<EditTitle/>} desc={<EditDescription/>} actions={<EditActions/>}>
        <SimpleForm redirect="show">
            <span style={viewStyles.fieldHeader}>
                    Product Type / Product Category / Brand
                    <TooltipIcon dataFor="ttTypeCat"/>
                    <ReactTooltip id="ttTypeCat" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <span style={viewStyles.fieldHeaderDescription}>
                            A <strong>Product Type</strong> is used for configuring the POS System to display products
                            to be sold dependent on your Location's DBA. In most cases, a business will only have one
                            Product Type.
                            <br/><br/>
                            <strong>Product Categories</strong> are used for sorting between Products on the POS
                            System so employees can browse quickly for the Product they need.
                            <br/><br/>
                            The Product Type, Category, and Product ID cannot be edited.
                        </span>
                    </ReactTooltip>
                </span>
            <TextField source="type" label="Type"
                       style={{...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight}}/>
            <TextField source="category" label="Current Category"
                       style={{...viewStyles.rowShowFieldWide, ...viewStyles.fieldLight}}/>
            <TextField source="category_id" label="ID"
                       style={{...viewStyles.rowShowField, ...styles.rowShowField, ...viewStyles.fieldLight}}/>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldDescription}>
                    Edit the <strong>Product Category</strong> or subcategory of this Product's Product Type.
                    <br/>
                    (If Category is not available, please exit and go
                    to <a href="#/ProductCategories/create" style={viewStyles.fieldLink}>Add Product Category</a>).
                    <TooltipIcon dataFor="ttCategory"/>
                    <ReactTooltip id="ttCategory" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <strong>Product Categories</strong> are used for sorting between Products on the POS System so
                        employees can browse quickly for the Product they need.
                    </ReactTooltip>
                </span>
            <DependentInput dependsOn="type">
                <ProductCategoryByTypeDependentSelectInput
                    {...props}
                    source="category_id"
                    dependentProp="dependsOnValue"
                    optionValue="id"
                    optionText="name"
                    label="Change Product Category"
                    validate={[required]}
                    allowEmpty/>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            </DependentInput>

            <TextField source="id" label="Product ID / Barcode #"/>

            <TextInput source="name" label="Name of Product" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Name</strong> of the the Product.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    In most cases, the <strong>Name</strong> of a Product is the same as its Product Units. You may have
                    an instance where a Product Unit's Name is different than its Product name.
                </ReactTooltip>
            </span>

            <TextInput source="brand" label="Brand" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>
                The <strong>Brand</strong> name.
                <TooltipIcon dataFor="ttBrand"/>
                <ReactTooltip id="ttBrand" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Some Products might have the same name but are made by different companies.
                    The <strong>Brand</strong> field differentiates between different products of the same name.
                    <br/><br/>
                    For example, Product "Banana" has a brand name of "Dole", while another Product "Banana" has a brand
                    name of "Chiquita".
                </ReactTooltip>
            </span>

            <NumberInput source="default_case_qty" label="Default Case Quantity" defaultValue={1} step={1}
                         validate={[required, minValue(0, 'Must not be a negative number')]}/>
            <span style={viewStyles.fieldDescription}>
                <strong>Default Case Qty</strong> defines the quantity of Pieces in a <em>Case</em> for the Product.
                <TooltipIcon dataFor="ttQty"/>
                <ReactTooltip id="ttQty" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Value must be at least 1. <strong>Default Case Qty</strong> defines how many Pieces are placed
                    on a pallet for shipping, or a specific total of pieces for packaging Cases to be sold.
                </ReactTooltip>
            </span>

            <SelectInput source="active" label="Display Product on POS"
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'}
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Whether Product is displayed on POS.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Selecting <em>Yes</em> will display the Product on the POS with the ability to be sold.
                    <br/>
                    Selecting <em>No</em> will remove the Product from the POS so it can not be selected or sold.
                    <br/><br/>
                    In some cases, you may want Products that will not be displayed because they are not at your
                    Location(s) yet. Or, a discontinued Product can be removed from your Location(s). This mitigates
                    human error from having employees accidently sell Products that are not in your Location(s).
                </ReactTooltip>
            </span>

            {<MakeProductUnitTable showButtons={{'create': false, 'view': false, 'edit': false, 'delete': false}}
                                   productView="edit"/>}

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the detail screen for this Product,
                where Product Units may be added or edited.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

        </SimpleForm>
    </EditWithType>
);



// WEBPACK FOOTER //
// ./src/Fuse/Product/product.js